import { useAction, useAtom, useCtx } from "@reatom/npm-react";
import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { RoutesPaths } from "src/routes/RoutesPaths";
import { selectedProductAtom, productSelectionStore } from "src/store/atoms";
import { isCanceledRequestError, toastHandler } from "src/util";

import { useInfinityScroll } from "../../hooks/useInfinityScroll";
import { useProductPageTranslate } from "../../hooks/useProductPageTranslate";
import { ProductTableData } from "../../store/fetchers";
import {
  flushProductSelectionRequest,
  saveProductSelectionRequest,
} from "../../widgets/ProductSamples/store/fetchers";
import { ProductPagination } from "../Pagination";
import { ProductCards } from "../ProductCards/ProductCards";
import { ProductsTable } from "../ProductsTable";
import css from "./ProductData.module.scss";

const SELECTION_SAVE_ERROR_TOAST = "productSelectionSaveError";

interface ProductDataProps {
  isTable?: boolean;
}
export const ProductData = ({ isTable }: ProductDataProps) => {
  const navigate = useNavigate();
  const ctx = useCtx();

  const {
    products,
    isLoading,
    isFetchLoading,
    isRefreshing,
    hasNext,
    onLoadMore,
  } = useInfinityScroll();

  const {
    refreshingData,
    selectionSaveErrorTitle,
    selectionSaveErrorContent,
    selectionSaveRetry,
  } = useProductPageTranslate();

  const [selectedIds] = useAtom(productSelectionStore.selectedIdsAtom);
  const [productSample] = useAtom(productSelectionStore.currentSelectionAtom);
  const setSelectedIds = useAction(productSelectionStore.setSelectedIdsAction);
  const saveSelection = useAction(saveProductSelectionRequest);
  const flushSelection = useAction(flushProductSelectionRequest);

  const setSelectProduct = useAction(selectedProductAtom);

  useEffect(() => {
    const flushOnHide = () => {
      if (document.visibilityState === "hidden") {
        flushSelection();
      }
    };

    document.addEventListener("visibilitychange", flushOnHide);

    return () => {
      document.removeEventListener("visibilitychange", flushOnHide);
      flushSelection();
    };
  }, [flushSelection]);

  const handleOnSelectProduct = useCallback(
    (product: ProductTableData) => {
      setSelectProduct(product.id);
      navigate(RoutesPaths.LK.PRODUCT(product.id));
    },
    [navigate, setSelectProduct],
  );

  const handleSelectionChange = useCallback(
    async (newSelectedIds: number[]) => {
      const savedSample = ctx.get(productSelectionStore.currentSelectionAtom);

      setSelectedIds(newSelectedIds);

      if (!savedSample) {
        return;
      }

      const stillOpenSample = () => {
        const current = ctx.get(productSelectionStore.currentSelectionAtom);

        return current?.id === savedSample.id ? current : null;
      };

      try {
        await saveSelection(savedSample.id, { productIds: newSelectedIds });

        toastHandler.dismiss(SELECTION_SAVE_ERROR_TOAST);
      } catch (error) {
        const activeSample = stillOpenSample();

        if (isCanceledRequestError(error) || !activeSample) {
          return;
        }

        setSelectedIds(activeSample.productIds ?? []);

        toastHandler.errorWithRetry({
          name: SELECTION_SAVE_ERROR_TOAST,
          title: selectionSaveErrorTitle,
          content: selectionSaveErrorContent,
          retryLabel: selectionSaveRetry,
          onRetry: () => handleSelectionChange(newSelectedIds),
        });
      }
    },
    [
      ctx,
      setSelectedIds,
      saveSelection,
      selectionSaveErrorTitle,
      selectionSaveErrorContent,
      selectionSaveRetry,
    ],
  );

  return (
    <div className={css.productDataContainer}>
      {isTable ? (
        <>
          <div className={css.tableContainer}>
            {(isRefreshing || isFetchLoading) && (
              <div className={css.refreshingOverlay}>
                <div className={css.refreshingSpinner}>
                  <div className={css.spinner}></div>
                  <span>{refreshingData}</span>
                </div>
              </div>
            )}
            <ProductsTable
              products={products}
              productSample={productSample}
              onRowClick={handleOnSelectProduct}
              isLoading={isLoading}
              selectedIds={selectedIds}
              setSelectedIds={handleSelectionChange}
            />
          </div>
          <ProductPagination isTable={true} />
        </>
      ) : (
        <ProductCards
          isLoading={isLoading}
          products={products}
          onLoadMore={onLoadMore}
          onClick={handleOnSelectProduct}
          isFetchLoading={isFetchLoading}
          hasNext={hasNext}
        />
      )}
    </div>
  );
};
